import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Quote, Sparkles, Send } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal, Overline } from "@/components/Reveal";

export const Testimonies = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", location: "", body: "" });
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/testimonies").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.body.trim()) {
      toast.error("Please share your name and testimony.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/testimonies", form);
      toast.success(data.message || "Thank you! Your testimony was submitted for review.");
      setForm({ name: "", location: "", body: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const field =
    "w-full px-4 py-3 rounded-xl bg-background border border-border focus:outline-none focus:ring-2 focus:ring-gold";
  const lbl = "block text-sm font-medium mb-1.5 text-foreground/80";

  return (
    <section
      id="testimonies"
      data-testid="testimonies-section"
      className="relative py-24 sm:py-32 bg-parchment dark:bg-ink text-foreground"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex items-center gap-3">
            <Sparkles className="text-gold-deep dark:text-gold-light" size={20} />
            <Overline>Testimonies</Overline>
          </div>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight max-w-3xl">
            Answered prayers & reports of His glory.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-foreground/70 leading-relaxed max-w-2xl">
            Read what God has done — and share your own testimony of breakthrough,
            healing, or a prophetic word that came to pass.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            {items.length === 0 ? (
              <Reveal>
                <div
                  data-testid="testimonies-empty"
                  className="rounded-2xl border border-dashed border-border p-10 text-center text-foreground/55"
                >
                  <Quote className="mx-auto mb-3 text-gold-deep dark:text-gold-light" size={28} />
                  Be the first to share what God has done in your life.
                </div>
              </Reveal>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {items.map((t, i) => (
                  <Reveal key={t.id} delay={0.05 * i}>
                    <motion.figure
                      whileHover={{ y: -4 }}
                      transition={{ type: "spring", stiffness: 200, damping: 18 }}
                      data-testid={`testimony-${t.id}`}
                      className="h-full rounded-2xl border border-border bg-card p-6"
                    >
                      <Quote className="text-gold-deep dark:text-gold-light" size={22} />
                      <blockquote className="mt-4 text-foreground/85 leading-relaxed">
                        {t.body}
                      </blockquote>
                      <figcaption className="mt-5 text-sm font-semibold">
                        {t.name}
                        {t.location && (
                          <span className="font-normal text-foreground/50">
                            {" "}· {t.location}
                          </span>
                        )}
                      </figcaption>
                    </motion.figure>
                  </Reveal>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <form
                data-testid="testimony-form"
                onSubmit={submit}
                className="rounded-3xl border border-gold/25 bg-card p-7 sm:p-8"
              >
                <h3 className="font-serif text-xl font-semibold">Share your testimony</h3>
                <p className="mt-1.5 text-sm text-foreground/60">
                  Testimonies are reviewed before they appear on the site.
                </p>
                <div className="mt-6 space-y-4">
                  <div>
                    <label className={lbl}>Name</label>
                    <input
                      data-testid="testimony-name"
                      className={field}
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className={lbl}>Location (optional)</label>
                    <input
                      data-testid="testimony-location"
                      className={field}
                      value={form.location}
                      onChange={update("location")}
                      placeholder="City, State"
                    />
                  </div>
                  <div>
                    <label className={lbl}>Your testimony</label>
                    <textarea
                      data-testid="testimony-body"
                      rows={5}
                      className={`${field} resize-none`}
                      value={form.body}
                      onChange={update("body")}
                      placeholder="Tell us what God has done…"
                    />
                  </div>
                </div>
                <button
                  data-testid="testimony-submit"
                  type="submit"
                  disabled={busy}
                  className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-ink text-parchment dark:bg-gold dark:text-ink font-semibold hover:opacity-90 transition-opacity disabled:opacity-60"
                >
                  {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                  Submit Testimony
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
